import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/auth/hooks/useAuth';
import type { AuthUser } from '@/lib/auth-api';
import { takeAuthFlash } from '@/lib/auth-flash';
import { authApi, getRetryAt } from '@/lib/auth-api';
import { toast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { PasskeyCancelled, passkeysAvailable } from '@/lib/passkeys';
import { usePrefetchedPasskeyOptions } from '@/auth/hooks/usePrefetchedPasskeyOptions';
import {
  CompanyAuthBrandHeader,
  SecuredByFias,
  useCompanyAuthBrand,
} from '@/auth/company/CompanyAuthBrand';

// Accepts "devuser1".."devuser10", "dev@localhost" (= devuser1),
// "dev2@localhost".."dev10@localhost", or an exact email/username match.
// Exported for unit testing; not part of any public API.
export function resolveDevUserIndex(identifier: string, devUsers: AuthUser[]): number | null {
  const value = identifier.trim().toLowerCase();
  if (!value) return null;

  const userMatch = value.match(/^devuser(\d{1,2})$/);
  if (userMatch) {
    const n = parseInt(userMatch[1], 10);
    if (n >= 1 && n <= devUsers.length) return n - 1;
  }

  const emailMatch = value.match(/^dev(\d*)@localhost$/);
  if (emailMatch) {
    const n = emailMatch[1] === '' ? 1 : parseInt(emailMatch[1], 10);
    if (n >= 1 && n <= devUsers.length) return n - 1;
  }

  const directIndex = devUsers.findIndex(
    (u) => u.email.toLowerCase() === value || u.username?.toLowerCase() === value,
  );
  return directIndex >= 0 ? directIndex : null;
}

/**
 * Generate an RFC-4122 v4 UUID for the recovery-login idempotency key. The
 * backend validates `opId` as a UUID and treats it as an idempotency token — a
 * timeout/retry with the SAME id can never burn two recovery codes. Prefers
 * `crypto.randomUUID`, with a `getRandomValues` fallback for older runtimes.
 * Exported for unit testing.
 */
export function generateOpId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return (
    `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-` +
    `${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
  );
}

/** Read the `code` off an unknown thrown value (AuthFetchError carries one). */
function errorCode(err: unknown): string | undefined {
  return typeof err === 'object' && err !== null && 'code' in err
    ? (err as { code?: string }).code
    : undefined;
}

/** Keep only digits, capped at 6 — for the TOTP inputs. */
function sanitizeOtp(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6);
}

type LoginStep = 'credentials' | 'mfa' | 'recovery';

/** Shared page chrome so each step doesn't re-declare the layout. */
function AuthShell({
  title,
  subtitle,
  children,
  belowCard,
}: {
  title: ReactNode;
  subtitle: ReactNode;
  children: ReactNode;
  belowCard?: ReactNode;
}) {
  // The Company-branded sign-in (ADR-024 S10) reuses this page as-is: only the
  // band above the card and "Secured by Fias" below it change.
  const companyBrand = useCompanyAuthBrand();
  return (
    <div className="min-h-screen flex flex-col bg-linear-to-b from-background to-muted/30">
      <div className="flex-1 flex items-center justify-center px-4 pb-20">
        <div className="w-full max-w-sm">
          {companyBrand && <CompanyAuthBrandHeader brand={companyBrand} />}
          <div className="text-center mb-8">
            {!companyBrand && (
              <img
                src="/fias-logo.png"
                alt="Fias"
                className="h-16 w-auto mx-auto mb-4 dark:invert"
              />
            )}
            <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          </div>

          <div className="bg-card rounded-xl border shadow-sm p-6">{children}</div>

          {belowCard}
          {companyBrand && <SecuredByFias />}
        </div>
      </div>

      <div className="py-4 text-center text-xs text-muted-foreground space-x-3">
        <Link to="/legal/terms" className="hover:text-foreground transition-colors">
          Terms of Service
        </Link>
        <span>·</span>
        <Link to="/legal/privacy" className="hover:text-foreground transition-colors">
          Privacy Policy
        </Link>
        <span>·</span>
        <Link to="/legal/acceptable-use" className="hover:text-foreground transition-colors">
          Acceptable Use Policy
        </Link>
      </div>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  if (!message) return null;
  return (
    <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3">
      <p className="text-sm text-destructive">{message}</p>
    </div>
  );
}

/**
 * Live countdown to a throttle's real unlock instant.
 *
 * A static "try again later" is useless to someone who has just lost their
 * phone and is transcribing a 16-character code from paper — they cannot tell
 * whether to wait ten seconds or an hour. The server sends an absolute instant
 * (not a duration) so this cannot drift with a slow request.
 */
function ThrottleCountdown({ retryAt }: { retryAt: string }) {
  const target = new Date(retryAt).getTime();
  const [remainingMs, setRemainingMs] = useState(() => Math.max(0, target - Date.now()));

  useEffect(() => {
    setRemainingMs(Math.max(0, target - Date.now()));
    const id = setInterval(() => setRemainingMs(Math.max(0, target - Date.now())), 1000);
    return () => clearInterval(id);
  }, [target]);

  if (!Number.isFinite(target) || remainingMs <= 0) return null;

  const totalSeconds = Math.ceil(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const label =
    hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;

  return (
    <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
      You can try again in <span className="font-mono tabular-nums">{label}</span>.
    </p>
  );
}

function NoticeBanner({ message }: { message: string }) {
  if (!message) return null;
  return (
    <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-3">
      <p className="text-sm text-green-700 dark:text-green-400">{message}</p>
    </div>
  );
}

export function LoginPage() {
  const [step, setStep] = useState<LoginStep>('credentials');
  const [identifier, setIdentifier] = useState(''); // email or username
  const [password, setPassword] = useState('');
  // The email resolved from the identifier at the credentials step, carried
  // forward so the recovery step can re-submit it (recovery-login takes an
  // explicit email, unlike the cookie-bound challenge step).
  const [resolvedEmail, setResolvedEmail] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState('');
  // Absolute unlock instant from a 429, when the server sent one.
  const [retryAt, setRetryAt] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  // Idempotency key for the current recovery attempt (see handleRecovery).
  const recoveryOpIdRef = useRef<{ code: string; opId: string } | null>(null);

  // An account made on a Company portal has no password yet (ADR-024 §10):
  // the address it was found under, to send the set-password code to.
  const [passwordNotSetEmail, setPasswordNotSetEmail] = useState('');

  const {
    signIn,
    signInWithPasskey,
    forgotPassword,
    mfaChallenge,
    mfaRecoveryLogin,
    isDevMode,
    devUsers,
    switchDevUser,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const companyBrand = useCompanyAuthBrand();
  // In the Company-branded flow the default destination is the Continue step.
  const from = (location.state as { from?: string })?.from || companyBrand?.continueTo || '/';

  // Confirmation shown after a flow that revoked the session and redirected
  // here — password reset (via router state) or password change (via the
  // one-shot auth flash, which survives ProtectedRoute's own redirect). Read
  // the flash once on mount so it clears after being shown.
  const [notice, setNotice] = useState<string>(
    () => (location.state as { message?: string })?.message ?? '',
  );
  useEffect(() => {
    const flash = takeAuthFlash();
    setNotice((current) => current || flash || '');
  }, []);

  const forgotPath = companyBrand?.paths.forgot ?? '/auth/forgot-password';

  // Ready before the click (see usePrefetchedPasskeyOptions).
  const canUsePasskeys = !isDevMode && passkeysAvailable();
  const passkeyOptions = usePrefetchedPasskeyOptions(
    () => authApi.passkeySignInOptions(),
    canUsePasskeys && step === 'credentials',
  );

  const handlePasskey = async () => {
    setError('');
    setNotice('');
    setPasswordNotSetEmail('');
    setIsLoading(true);
    try {
      await signInWithPasskey(await passkeyOptions.take());
      navigate(from, { replace: true });
    } catch (err) {
      passkeyOptions.prefetch();
      if (err instanceof PasskeyCancelled) {
        setNotice('No passkey was used. Sign in with your password, or try the passkey again.');
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // The same emailed step as "Forgot password?", named for what it is here.
  const handleSendSetPasswordCode = async () => {
    setError('');
    setIsLoading(true);
    try {
      await forgotPassword(passwordNotSetEmail);
      navigate(forgotPath, {
        state: { mode: 'set', email: passwordNotSetEmail, codeSent: true },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCredentials = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordNotSetEmail('');
    setIsLoading(true);
    let attemptedEmail = identifier;

    try {
      if (isDevMode) {
        const index = resolveDevUserIndex(identifier, devUsers);
        if (index === null) {
          throw new Error('Unknown dev user. Try devuser1, devuser2, … or dev@localhost.');
        }
        await switchDevUser(index);
        navigate(from, { replace: true });
        return;
      }

      // Check if identifier looks like an email
      const isEmail = identifier.includes('@');
      let emailToUse = identifier;

      if (!isEmail) {
        // Resolve username to email
        try {
          const response = await api.get<{ email: string }>('/auth/resolve-username', {
            params: { username: identifier },
          });
          emailToUse = response.data.email;
        } catch {
          // API returns 401 for invalid/not found usernames
          throw new Error('Invalid username or password');
        }
      }

      attemptedEmail = emailToUse;
      const result = await signIn(emailToUse, password);
      if (result.status === 'mfa_required') {
        // Password was correct but no session was established. Advance to the
        // TOTP step; keep `password` in memory so the recovery step can use it.
        setResolvedEmail(emailToUse);
        setMfaCode('');
        setNotice('');
        setError('');
        setStep('mfa');
        return;
      }

      navigate(from, { replace: true });
    } catch (err) {
      if (errorCode(err) === 'PASSWORD_NOT_SET') {
        setPasswordNotSetEmail(attemptedEmail);
        return;
      }
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfa = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await mfaChallenge(mfaCode);
      navigate(from, { replace: true });
    } catch (err) {
      if (errorCode(err) === 'MFA_CHALLENGE_EXPIRED') {
        // The Cognito challenge session lapsed (cookie cleared server-side).
        // Return to the password step; the user re-authenticates from scratch.
        setStep('credentials');
        setMfaCode('');
        setPassword('');
        setError('');
        setNotice('Your verification window expired. Please sign in again.');
      } else {
        // MFA_CODE_MISMATCH (and anything else) — stay on the step to retry.
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecovery = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setRetryAt(undefined);
    setIsLoading(true);

    try {
      // One opId per logical recovery attempt: reuse it across resubmits of the
      // SAME code (so a retryable 503 / timeout resumes the idempotent saga
      // rather than minting a new op that strands the first one), and mint a
      // fresh one only when the entered code changes.
      const trimmedCode = recoveryCode.trim();
      if (!recoveryOpIdRef.current || recoveryOpIdRef.current.code !== trimmedCode) {
        recoveryOpIdRef.current = { code: trimmedCode, opId: generateOpId() };
      }
      const result = await mfaRecoveryLogin(
        resolvedEmail,
        password,
        trimmedCode,
        recoveryOpIdRef.current.opId,
      );
      // Signed in with the account fully intact — two-factor is still on and one
      // backup code has been spent. Three things must reach the user here and
      // none of them can wait for a settings visit: that a finite code was
      // consumed, that their OTHER devices were signed out (a real revocation
      // they will otherwise discover as a mystery logout), and — when the set is
      // nearly empty — a way to act on it rather than just a number.
      const left = result.recoveryCodesRemaining;
      const lowCodes = left !== undefined && left <= 3;
      toast({
        title: 'Signed in with a backup code',
        description:
          (left === undefined
            ? 'That code has been used up.'
            : `That code has been used up — ${left} left.`) +
          ' Two-factor is still on, and we signed you out of your other devices.',
        ...(lowCodes
          ? {
              action: (
                <ToastAction
                  altText="Generate new backup codes"
                  onClick={() => navigate('/account/settings')}
                >
                  Generate new codes
                </ToastAction>
              ),
            }
          : {}),
      });
      navigate(from, { replace: true });
    } catch (err) {
      // A throttle carries the real unlock instant; hold it so the form can
      // count down instead of showing a message that is stale the moment it
      // renders.
      setRetryAt(getRetryAt(err));
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // ---- MFA challenge step ---------------------------------------------------
  if (step === 'mfa') {
    return (
      <AuthShell
        title="Two-factor authentication"
        subtitle="Enter the 6-digit code from your authenticator app"
      >
        <form onSubmit={handleMfa} className="space-y-4">
          <ErrorBanner message={error} />

          <div className="space-y-2">
            <Label htmlFor="mfa-code">Authentication code</Label>
            <Input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              placeholder="123456"
              value={mfaCode}
              onChange={(e) => setMfaCode(sanitizeOtp(e.target.value))}
              required
              autoComplete="one-time-code"
              autoFocus
              disabled={isLoading}
              maxLength={6}
              className="h-11 text-center font-mono tracking-[0.4em] text-lg"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-11"
            disabled={isLoading || mfaCode.length !== 6}
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </Button>

          <div className="space-y-2 pt-1 text-center">
            <button
              type="button"
              className="text-sm text-primary font-medium hover:underline"
              onClick={() => {
                setStep('recovery');
                setRecoveryCode('');
                setError('');
              }}
              disabled={isLoading}
            >
              Use a recovery code instead
            </button>
            <p className="text-sm text-muted-foreground">
              <button
                type="button"
                className="hover:text-foreground transition-colors"
                onClick={() => {
                  setStep('credentials');
                  setMfaCode('');
                  setPassword('');
                  setError('');
                }}
                disabled={isLoading}
              >
                Back to sign in
              </button>
            </p>
          </div>
        </form>
      </AuthShell>
    );
  }

  // ---- Recovery-code step ---------------------------------------------------
  if (step === 'recovery') {
    return (
      <AuthShell
        title="Use a recovery code"
        subtitle="For when you can’t reach your authenticator app"
      >
        <form onSubmit={handleRecovery} className="space-y-4">
          <ErrorBanner message={error} />
          {retryAt && <ThrottleCountdown retryAt={retryAt} />}

          {/* Deliberately reassuring, not alarming: a recovery code spends
              itself and nothing else. The old copy warned that it would turn
              two-factor OFF, which was true of the old behaviour and is exactly
              the behaviour that made ten codes worth one. */}
          <div className="rounded-lg bg-muted p-3">
            <p className="text-sm text-muted-foreground">
              Each recovery code works <strong>once</strong>. Two-factor authentication stays on and
              your other codes keep working — you’ll just be signed straight in.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="recovery-code">Recovery code</Label>
            <Input
              id="recovery-code"
              type="text"
              placeholder="XXXX-XXXX-XXXX-XXXX"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              required
              autoComplete="one-time-code"
              autoFocus
              disabled={isLoading}
              className="h-11 text-center font-mono tracking-widest"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-11"
            disabled={isLoading || recoveryCode.trim().length === 0}
          >
            {isLoading ? 'Verifying...' : 'Use recovery code'}
          </Button>

          <p className="text-sm text-muted-foreground text-center pt-1">
            <button
              type="button"
              className="hover:text-foreground transition-colors"
              onClick={() => {
                setStep('mfa');
                setError('');
              }}
              disabled={isLoading}
            >
              Back to code entry
            </button>
          </p>
        </form>
      </AuthShell>
    );
  }

  // ---- Credentials step (default) -------------------------------------------
  return (
    <AuthShell
      title={
        <>
          Welcome back
          {isDevMode && (
            <span className="ml-2 align-middle text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-950/40 border border-amber-500/30 rounded px-1.5 py-0.5">
              DEV
            </span>
          )}
        </>
      }
      subtitle={
        isDevMode
          ? 'Type a dev user (e.g. devuser1) — no password needed.'
          : companyBrand
            ? `Sign in to continue to ${companyBrand.name}`
            : 'Sign in to continue to Fias'
      }
      belowCard={
        // A members-only Company portal offers sign-in only (ADR-024 §4.4.1):
        // its people arrive through workspace invitations.
        (!companyBrand || companyBrand.allowRegistration) && (
          <p className="text-center text-sm text-muted-foreground mt-6">
            Don't have an account?{' '}
            <Link
              to={companyBrand?.paths.register ?? '/auth/signup'}
              className="text-primary font-medium hover:underline"
            >
              Sign up
            </Link>
          </p>
        )
      }
    >
      <form onSubmit={handleCredentials} className="space-y-4">
        <NoticeBanner message={notice} />
        <ErrorBanner message={error} />
        {passwordNotSetEmail && (
          <div className="rounded-lg border bg-muted/40 p-3 space-y-2" role="alert">
            <p className="text-sm font-medium text-foreground">You must set a password</p>
            <p className="text-sm text-muted-foreground">
              Your Fias account was made on a Company's site, without a password. Set one to sign in
              here — we'll email a code to {passwordNotSetEmail}.
            </p>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() => void handleSendSetPasswordCode()}
              disabled={isLoading}
            >
              Email me a code
            </Button>
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="identifier">{isDevMode ? 'Dev user' : 'Email or Username'}</Label>
            <Input
              id="identifier"
              type="text"
              placeholder={isDevMode ? 'devuser1' : 'you@example.com'}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              autoComplete="username"
              disabled={isLoading}
              className="h-11"
            />
          </div>

          {!isDevMode && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to={forgotPath}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                id="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={isLoading}
                className="h-11"
              />
            </div>
          )}
        </div>

        <Button type="submit" className="w-full h-11 mt-2" disabled={isLoading}>
          {isLoading ? 'Signing in...' : 'Sign in'}
        </Button>

        {canUsePasskeys && (
          <>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" /> or{' '}
              <span className="h-px flex-1 bg-border" />
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full h-11"
              onClick={() => void handlePasskey()}
              disabled={isLoading}
            >
              Sign in with a passkey
            </Button>
          </>
        )}
      </form>
    </AuthShell>
  );
}
